import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Components and UI
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  Field,
  Input,
  Spinner,
  Button,
  makeStyles,
  shorthands,
  tokens,
  useRestoreFocusSource,
  DrawerFooter,
  Checkbox,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";
import { useTranslation } from "react-i18next";

import UserAvatar from "./UserAvatar";
import ImagePicker from "../../../shared/components/ImagePicker";
import LanguageComboBox from "../../../shared/components/LanguageComboBox";
import SiteComboBox from "../../sites/components/SiteComboBox";
import UsersComboBox from "../../users/components/UserComboBox";

// Styles
import "../../../styles/animations.css";

// Business logic
import { useSelectedUsersContext } from "../context/SelectedUsersContext";
import { useUserQuery } from "../hooks/useUserQueries";
import { useCreateUser, useUpdateUser, useUpdateUserPhoto } from "../hooks/useUserMutations";
import { userSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";

// Variables
const useStyles = makeStyles({
  root: {
    overflow: "visible",

    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },

  content: {
    flex: "1",
    padding: "16px",

    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gridRowGap: tokens.spacingVerticalXXL,
    gridAutoRows: "max-content",
  },

  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
});

function UserDrawer({ isOpen, setIsOpen, newUser }) {
  const { notify } = useNotify();
  const queryClient = useQueryClient();
  const styles = useStyles();
  const [currentUser, setCurrentUser] = useState(null);
  const { value: selectedUsers } = useSelectedUsersContext();
  const { t } = useTranslation();
  const restoreFocusSourceAttributes = useRestoreFocusSource();

  const public_id = selectedUsers?.[0]?.public_id ?? null;

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateUser = useItemValidation(userSchema.$defs.user);

  // Errors
  const [errors, setErrors] = useState([]);

  // Image
  const [pendingFile, setPendingFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef();

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useUserQuery(public_id, isOpen && !newUser);
  const {
    mutate: createUser,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useCreateUser(
    "users",
    (data) => {
      if (pendingFile) updateUserPhoto({ item: data, file: pendingFile });
      else {
        notify(t("item.messages.created", { name: data.name }), "success");
        handleClose();
      }
    }, // handleSucess
    null, // handleError not required since there is a conditional render for the ErrorMessageBar based on errorUpdating
    true, // Extended log to console
  );
  const {
    mutate: updateUser,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateUser(
    "users",
    (data) => {
      if (pendingFile) updateUserPhoto({ item: data, file: pendingFile });
      else {
        notify(t("item.messages.saved", { name: data.name }), "success");
        handleClose();
      }
    }, // handleSucess
    null, // handleError not required since there is a conditional render for the ErrorMessageBar based on errorUpdating,
    false, // No extended logs
  );
  // should be called from createUser / updateUser only
  const {
    mutate: updateUserPhoto,
    isPending: isUpdatingPhoto,
    error: errorUpdatingPhoto,
    reset: resetUpdatingPhoto,
  } = useUpdateUserPhoto(
    "users",
    (data) => {
      notify(t(newUser ? "item.messages.created" : "item.messages.updated", { name: data.name }), "success");
      setPendingFile(null);
      handleClose();
    },
    (err) => {
      notify(
        t(newUser ? "item.messages.createdButIcon" : "item.messages.updatedButIcon", {
          name: currentUser.name,
        }),
        "warning",
      );
      handleClose();
    },
  );

  // 2. UseEffects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (errorCreating) {
      newErrors.push({ message: errorCreating?.message || t("generic.error"), type: "error", details: errorCreating });
    }
    if (errorUpdating) {
      newErrors.push({ message: errorUpdating?.message || t("generic.error"), type: "error", details: errorUpdating });
    }
    if (errorUpdatingPhoto) {
      newErrors.push({
        message: errorUpdatingPhoto?.message || t("generic.error"),
        type: "error",
        details: errorUpdatingPhoto,
      });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, errorCreating, errorUpdating, errorUpdatingPhoto, t]);

  // 2. UseEffects
  useEffect(() => {
    if (!isOpen) return;
    if (newUser) {
      const initialUser = { id: 0, name: "", is_agent: 0 };
      setCurrentUser(initialUser);
      setValidationErrors(validateUser(initialUser));
    } else if (isSuccess && data) {
      setCurrentUser(data);
    }
  }, [isOpen, newUser, isSuccess, data]);

  useEffect(() => {
    if (!pendingFile) return;
    const tempBlobUrl = URL.createObjectURL(pendingFile);
    setImageSrc(tempBlobUrl);
    return () => URL.revokeObjectURL(tempBlobUrl);
  }, [pendingFile]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["user", public_id] });
    resetCreating();
    resetUpdating();
    resetUpdatingPhoto();
    setCurrentUser(null);
    setValidationErrors({});
    setPendingFile(null);
    setImageSrc(null);
    setErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setIsOpen(false);
  };

  const handleFileChange = (file) => {
    if (file) {
      setPendingFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleOpenChange = (_, data) => {
    console.log(data.type);
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const updateField = (field, value) => {
    //console.log(`Setting field ${field} from ${editingUser?.[field]} to ${value} (${typeof value})`);
    const updated = { ...currentUser, [field]: value };
    setCurrentUser(updated);
    setValidationErrors(validateUser(updated));
    //console.log(validationErrors, typeof validationErrors);
  };

  return (
    <div className={styles.root}>
      <Drawer
        type="overlay"
        position="end"
        separator
        size="medium"
        open={isOpen}
        onOpenChange={handleOpenChange}
        {...restoreFocusSourceAttributes}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={handleClose} />}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <UserAvatar user={currentUser} size={48} url={imageSrc} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{newUser ? t("user.new") : (`${currentUser?.name}` ?? "")}</span>
                <ImagePicker
                  label={t("generic.imageChange")}
                  onFileSelected={handleFileChange}
                  fileInputRef={fileInputRef}
                />
              </div>
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newUser && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1011,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody>
          <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
          <Field
            className={styles.field}
            label={t("generic.name")}
            validationState={validationErrors.name ? "error" : undefined}
            required
          >
            <Input
              id="name"
              maxLength="120"
              value={currentUser?.name ?? ""}
              onChange={(e) => updateField("name", e.target.value)}
            />
          </Field>
          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("generic.fname")}
              style={{ flex: 1 }}
              validationState={validationErrors.fname ? "error" : undefined}
              required
            >
              <Input
                id="fname"
                maxLength="50"
                validationState={validationErrors.fname ? "error" : undefined}
                required
                value={currentUser?.fname ?? ""}
                onChange={(e) => updateField("fname", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("generic.lname")}
              style={{ flex: 1 }}
              validationState={validationErrors.lname ? "error" : undefined}
              required
            >
              <Input
                id="lname"
                maxLength="50"
                value={currentUser?.lname ?? ""}
                onChange={(e) => updateField("lname", e.target.value)}
                required
              />
            </Field>
          </div>
          <Field
            className={styles.field}
            label={t("generic.email")}
            validationState={validationErrors.email ? "error" : undefined}
            required
          >
            <Input
              id="email"
              maxLength="254"
              value={currentUser?.email ?? ""}
              onChange={(e) => updateField("email", e.target.value)}
            />
          </Field>
          <Field
            className={styles.field}
            label={t("generic.positionTitle")}
            validationState={validationErrors.title ? "error" : undefined}
          >
            <Input
              id="title"
              maxLength="75"
              value={currentUser?.title ?? ""}
              onChange={(e) => updateField("title", e.target.value)}
            />
          </Field>
          <Field
            className={styles.field}
            label={t("generic.manager")}
            validationState={validationErrors.manager_id ? "error" : undefined}
          >
            <UsersComboBox value={currentUser?.manager_id} onOptionSelect={(e, data) => updateField("manager_id", e)} />
          </Field>
          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("generic.phone")}
              style={{ flex: 1 }}
              validationState={validationErrors.phone ? "error" : undefined}
            >
              <Input
                id="phone"
                maxLength="15"
                value={currentUser?.phone ?? ""}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("generic.mobile")}
              style={{ flex: 1 }}
              validationState={validationErrors.mobile ? "error" : undefined}
            >
              <Input
                id="mobile"
                maxLength="15"
                value={currentUser?.mobile ?? ""}
                onChange={(e) => updateField("mobile", e.target.value)}
              />
            </Field>
          </div>
          <Field
            className={styles.field}
            label={t("generic.site")}
            required
            validationState={validationErrors.site_id ? "error" : undefined}
          >
            <SiteComboBox
              positioning={{
                position: "below", // ensures dropdown appears under the trigger
                align: "start", // aligns left edge of dropdown with trigger
                pinned: true, // prevents auto-flipping to left/right
              }}
              listbox={{
                style: {
                  maxHeight: "300px", // prevent full-height dropdown
                  overflowY: "auto", // enable scroll if needed
                },
              }}
              value={currentUser?.site_id}
              onOptionSelect={(e, data) => updateField("site_id", e)}
            />
          </Field>
          <Field className={styles.field} label={t("generic.language")}>
            <LanguageComboBox value={currentUser?.language} onOptionSelect={(e, data) => updateField("language", e)} />
          </Field>
          <Checkbox
            checked={Number(currentUser?.is_agent) > 0}
            label="Agent"
            style={{ margin: "6px 0" }}
            onChange={(e, data) => updateField("is_agent", data.checked ? 1 : 0)}
          />
          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              icon={isCreating || isUpdating || isUpdatingPhoto ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || isUpdatingPhoto || Object.keys(validationErrors).length > 0}
              onClick={() => (newUser ? createUser(currentUser, handleClose) : updateUser(currentUser, handleClose))}
            >
              {newUser ? t("generic.create") : t("generic.update")}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {t("generic.cancel")}
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  ); // return null if the value isOpen is false
}

export default UserDrawer;
